// src/wizard/useWizardEngine.js
import { useEffect, useRef, useState } from "react";
import { questions } from "./questions";
import { featureMatches } from "./matchers";
import { calcMatches } from "./productFilter";

export const useWizardEngine = ({ products, caseApi }) => {
  const [step, setStep] = useState(-1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [alternativeProducts, setAlternativeProducts] = useState([]);
  const [caseId, setCaseId] = useState("");

  const submittedRef = useRef(false);

  const hasExact = filteredProducts.length > 0;
  const hasAlternative = alternativeProducts.length > 0;

  const recompute = (selections) => {
    const { exactMatches, alternativeMatches } = calcMatches({
      products,
      selections,
      featureMatches,
    });

    setFilteredProducts(exactMatches);
    setAlternativeProducts(alternativeMatches);
  };

  const start = async () => {
    const { case_id } = await caseApi.createCase();
    setCaseId(case_id);
    submittedRef.current = false;
    setStep(0);
  };

  const select = async (key, value) => {
    const newSelections = { ...selectedOptions, [key]: value };
    setSelectedOptions(newSelections);

    recompute(newSelections);

    if (caseId) {
      caseApi.saveCase(caseId, newSelections).catch(console.error);
    }

    setStep((s) => s + 1);
  };

  const back = () => {
    if (step <= 0) return;

    const prevKey = questions[step - 1]?.key;
    setSelectedOptions((prev) => {
      const updated = { ...prev };
      delete updated[prevKey];
      // 1つ戻した状態で再計算
      recompute(updated);
      return updated;
    });

    setStep((s) => s - 1);
  };

  // index 番目の質問に戻る（それ以降の回答はいったん外す）
  const jumpTo = (index) => {
    if (index < 0 || index >= step) return;
    const removeKeys = questions.slice(index).map((q) => q.key);
    const updated = { ...selectedOptions };
    removeKeys.forEach((k) => delete updated[k]);
    setSelectedOptions(updated);
    recompute(updated);
    setStep(index);
  };

  const relaxConditions = () => {
    const relaxed = { ...selectedOptions };

    ["resolution", "aiFeatures", "lowLightPerformance"].forEach((k) => {
      if (relaxed[k] && relaxed[k] !== "こだわらない") {
        relaxed[k] = "こだわらない";
      }
    });

    setSelectedOptions(relaxed);
    recompute(relaxed);
  };

  const reset = () => {
    setStep(-1);
    setSelectedOptions({});
    setFilteredProducts([]);
    setAlternativeProducts([]);
    setCaseId("");
    submittedRef.current = false;
  };

  useEffect(() => {
    if (caseId && step >= questions.length && !submittedRef.current) {
      submittedRef.current = true;

      const proposedCameraCount =
        filteredProducts.length > 0 ? filteredProducts.length : alternativeProducts.length;

      caseApi.submitCase(caseId, proposedCameraCount).catch(console.error);

      console.log("SUBMITTED:", {
        caseId,
        proposedCameraCount,
      });
    }
  }, [caseId, step, caseApi, filteredProducts.length, alternativeProducts.length]);
  return {
    questions,

    step,
    setStep,

    selectedOptions,
    setSelectedOptions,

    filteredProducts,
    alternativeProducts,

    hasExact,
    hasAlternative,

    caseId,

    start,
    select,
    back,
    jumpTo,
    relaxConditions,
    reset,
  };
};
